/**
 * @coeldery/role-tab-bar —— 內置 SVG icon 集（零外部 icon library）
 *
 * 每個 icon 都係 28×28（viewBox 24×24）純 SVG；顏色一律 `var(--color-primary)`（active）／
 * `var(--color-text-secondary)`（inactive）—— **唔准 hardcode hex**。
 * 為何自帶 icon 而唔用 icon lib：同 `@coeldery/bottom-tab-bar` 一樣，行動端要零額外依賴。
 */

export type TabIcon =
  | 'me'
  | 'orders'
  | 'shipment'
  | 'stock'
  | 'incoming'
  | 'pickup'
  | 'fees'
  | 'home'
  | 'purchase'
  | 'outbound'
  | 'disposition';

const col = (active: boolean): string =>
  active ? 'var(--color-primary)' : 'var(--color-text-secondary)';

/** 統一外框（28px）；`aria-hidden` 由外層負責語意（文字 label 已足夠） */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

export function TabIconSvg({ name, active }: { name: TabIcon; active: boolean }) {
  const c = col(active);
  switch (name) {
    case 'me': // 人（我嘅資料）
      return (
        <Frame>
          <circle cx="12" cy="8" r="3.4" fill={c} />
          <path d="M4.5 20c0-3.6 3.4-6.4 7.5-6.4s7.5 2.8 7.5 6.4" stroke={c} strokeWidth="2" strokeLinecap="round" />
        </Frame>
      );
    case 'orders': // 單（訂單／採購單）
      return (
        <Frame>
          <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke={c} strokeWidth="2" />
          <path d="M8 9h8M8 13h8M8 17h5" stroke={c} strokeWidth="2" strokeLinecap="round" />
        </Frame>
      );
    case 'shipment': // 出貨（箱 + 向上箭嘴）
      return (
        <Frame>
          <path d="M12 3l7 3.5v2.5H5V6.5L12 3z" fill={c} />
          <rect x="5" y="10.5" width="14" height="10" rx="1.6" stroke={c} strokeWidth="2" />
          <path d="M12 13.6v4.4M12 13.6l-2.2 2.2M12 13.6l2.2 2.2" stroke={c} strokeWidth="2" strokeLinecap="round" />
        </Frame>
      );
    case 'stock': // 庫存（疊箱）
      return (
        <Frame>
          <rect x="4" y="13" width="7" height="7" rx="1.4" fill={c} />
          <rect x="13" y="13" width="7" height="7" rx="1.4" fill={c} />
          <rect x="8.5" y="4.5" width="7" height="7" rx="1.4" fill={c} />
        </Frame>
      );
    case 'incoming': // 到貨（箱 + 向下箭嘴）
      return (
        <Frame>
          <path d="M4 5h16M5.5 5v4h13V5" stroke={c} strokeWidth="2" strokeLinecap="round" />
          <rect x="6" y="12.5" width="12" height="8" rx="1.6" stroke={c} strokeWidth="2" />
          <path d="M12 14.5v4.2M12 18.7l-2-2M12 18.7l2-2" stroke={c} strokeWidth="2" strokeLinecap="round" />
        </Frame>
      );
    case 'pickup': // 出貨指示（手推車）
      return (
        <Frame>
          <path d="M3 4h2.2l2.4 9.6h9.8L19.6 7H6" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="9" cy="18.5" r="1.8" fill={c} />
          <circle cx="16.5" cy="18.5" r="1.8" fill={c} />
        </Frame>
      );
    case 'fees': // 倉租（銀紙）
      return (
        <Frame>
          <rect x="3" y="6" width="18" height="12" rx="2.4" stroke={c} strokeWidth="2" />
          <circle cx="12" cy="12" r="3" stroke={c} strokeWidth="2" />
        </Frame>
      );
    case 'home': // 總覽
      return (
        <Frame>
          <path d="M12 3.5l8 6.5v9.5H4V10l8-6.5z" stroke={c} strokeWidth="2" strokeLinejoin="round" />
          <path d="M10 19.5v-5h4v5" stroke={c} strokeWidth="2" strokeLinejoin="round" />
        </Frame>
      );
    case 'purchase': // 向供應商落單（單 + 加號）
      return (
        <Frame>
          <rect x="4" y="4" width="13" height="16" rx="2.4" stroke={c} strokeWidth="2" />
          <path d="M7.5 9h6M7.5 13h4" stroke={c} strokeWidth="2" strokeLinecap="round" />
          <path d="M18.5 15v6M15.5 18h6" stroke={c} strokeWidth="2" strokeLinecap="round" />
        </Frame>
      );
    case 'outbound': // 向物流落出庫指示（箱 + 右箭嘴）
      return (
        <Frame>
          <rect x="3" y="6" width="10" height="12" rx="1.8" stroke={c} strokeWidth="2" />
          <path d="M14 12h7M21 12l-3-3M21 12l-3 3" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </Frame>
      );
    case 'disposition': // 貨去向（分岔）
      return (
        <Frame>
          <path d="M4 12h4" stroke={c} strokeWidth="2" strokeLinecap="round" />
          <path d="M8 12l6-5h6M8 12l6 5h6" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="4" cy="12" r="1.6" fill={c} />
        </Frame>
      );
  }
}
